import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Stack,
  Typography,
  Chip,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Menu,
  Skeleton,
  Dialog,
  DialogContent,
  Divider,
  useMediaQuery
} from '@mui/material';
import { useTheme, keyframes } from '@mui/material/styles';
import {
  DownloadDone,
  Groups,
  Favorite,
  FavoriteBorder,
  Add,
  Whatshot,
  Bolt,
  Tune,
  ChatBubbleOutline,
  EmojiEvents,
  Close,
  MoreVert,
  EditOutlined,
  DeleteOutline,
  FlagOutlined
} from '@mui/icons-material';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import AppBarContext from 'context/AppBarContext';
import {
  SHARED_ROUTINES,
  ADOPT_ROUTINE,
  REPORT_ROUTINE,
  MY_WEEKLY_ROUTINE,
  TOGGLE_ROUTINE_LIKE,
  DELETE_SHARED_ROUTINE,
  SUGGESTED_ATHLETES,
  MY_FOLLOWERS_COUNT,
  FOLLOW_USER,
  UNFOLLOW_USER
} from 'api/graphql/play';
import { createSnack } from 'common/utils/general';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import uiSlice from 'store/modules/ui';
import { MUSCLE_GROUPS, muscleLabel } from 'components/play/constants';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import ContentRenderer from 'components/shared/content/ContentRenderer';
import PostComments from 'components/play/PostComments';
import RoutineCommentsDialog from 'components/play/RoutineCommentsDialog';
import PublishRoutineDialog from 'components/play/PublishRoutineDialog';
import PlayAthleteModal from 'components/play/PlayAthleteModal';
import OnlineAvatar from 'components/play/OnlineAvatar';
import RoutineLikersDialog from 'components/play/RoutineLikersDialog';
import SharedRoutineWeek from 'components/play/SharedRoutineWeek';
import routes from 'common/routes';

const ago = (iso) => {
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true, locale: es });
  } catch {
    return '';
  }
};

// Lift sutil de card (design system): sólo transform, curva calibrada.
const cardHoverSx = {
  transition: 'transform 220ms cubic-bezier(0.32,0.72,0,1), border-color 220ms cubic-bezier(0.32,0.72,0,1)',
  '&:hover': { transform: 'translateY(-2px)', borderColor: 'hairline.strong' }
};

// "Pop" del corazón al dar like (solo transform, con el overshoot de la casa).
const heartPop = keyframes`
  0% { transform: scale(1); }
  40% { transform: scale(1.35); }
  100% { transform: scale(1); }
`;

const COACH_ROLES = ['TRAINER', 'ADMINISTRATOR'];

/**
 * OTRIX Play — foro social (Facebook/Instagram) con la paleta OTRIX (off-black + dorado como
 * acento pequeño): hero de "Publicar mi rutina", feed con descripción rica (SIN título separado)
 * + comentarios inline, orden por recientes/adoptadas, y columna de atletas con presencia "en
 * línea" y seguir. Los perfiles abren un modal.
 */
function SharedRoutinesForum() {
  const { t } = useTranslation(['play']);
  const { setAppBarTitle } = useContext(AppBarContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const me = useSelector((s) => s.auth.user);
  const [muscleFocus, setMuscleFocus] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [commentsTarget, setCommentsTarget] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerRoutine, setComposerRoutine] = useState(null);
  const [athleteId, setAthleteId] = useState(null);
  const [athletesDialogOpen, setAthletesDialogOpen] = useState(false);
  const [likersTarget, setLikersTarget] = useState(null);
  const [postMenu, setPostMenu] = useState({ anchor: null, routine: null });

  useEffect(() => {
    setAppBarTitle(t('forum.title', { ns: 'play' }));
  }, [setAppBarTitle, t]);

  const feedVars = { muscle_focus: muscleFocus || undefined };
  const { data, loading } = useQuery(SHARED_ROUTINES, { variables: feedVars, fetchPolicy: 'cache-and-network' });
  const { data: athletesData } = useQuery(SUGGESTED_ATHLETES, { fetchPolicy: 'cache-and-network' });
  const { data: followersData } = useQuery(MY_FOLLOWERS_COUNT, { fetchPolicy: 'cache-and-network' });

  const feedRefetch = [{ query: SHARED_ROUTINES, variables: feedVars }];
  const [adopt] = useMutation(ADOPT_ROUTINE, { refetchQueries: [{ query: MY_WEEKLY_ROUTINE }] });
  const [report] = useMutation(REPORT_ROUTINE, { refetchQueries: feedRefetch });
  const [toggleLike] = useMutation(TOGGLE_ROUTINE_LIKE);
  const [removeRoutine] = useMutation(DELETE_SHARED_ROUTINE, { refetchQueries: feedRefetch });
  const [follow] = useMutation(FOLLOW_USER, { refetchQueries: [{ query: SUGGESTED_ATHLETES }, { query: MY_FOLLOWERS_COUNT }] });
  const [unfollow] = useMutation(UNFOLLOW_USER, { refetchQueries: [{ query: SUGGESTED_ATHLETES }, { query: MY_FOLLOWERS_COUNT }] });

  const routines = useMemo(() => data?.sharedRoutines || [], [data]);
  const athletes = athletesData?.suggestedAthletes || [];
  const followersCount = followersData?.myFollowersCount || 0;

  // Orden client-side sobre la lista ya cargada (sin ida al backend).
  const sortedRoutines = useMemo(() => {
    const arr = [...routines];
    if (sortBy === 'adopted') {
      arr.sort((a, b) => (b.adopt_count || 0) - (a.adopt_count || 0) || new Date(b.created_at) - new Date(a.created_at));
    } else {
      arr.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    return arr;
  }, [routines, sortBy]);

  const toast = (message, variant = 'success') =>
    dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: `fr-${Date.now()}`, variant, message })));

  // Like OPTIMISTA: el corazón responde al instante (la mutation confirma o revierte sola).
  const handleLike = async (routine) => {
    try {
      await toggleLike({
        variables: { id: routine.id },
        optimisticResponse: {
          toggleRoutineLike: {
            __typename: 'SharedRoutine',
            id: routine.id,
            like_count: routine.is_liked ? Math.max(0, (routine.like_count || 1) - 1) : (routine.like_count || 0) + 1,
            is_liked: !routine.is_liked
          }
        }
      });
    } catch {
      toast(t('forum.errorReport', { ns: 'play' }), 'error');
    }
  };

  const handleAdopt = async (id) => {
    try {
      await adopt({ variables: { id } });
      toast(t('forum.adopted', { ns: 'play' }));
      navigate(routes.playRoutine.path);
    } catch {
      toast(t('forum.errorAdopt', { ns: 'play' }), 'error');
    }
  };

  const handleReport = async (id) => {
    try {
      await report({ variables: { id, reason: null } });
      toast(t('forum.reported', { ns: 'play' }), 'info');
    } catch {
      toast(t('forum.errorReport', { ns: 'play' }), 'error');
    }
  };

  const handleDelete = async (id) => {
    try {
      await removeRoutine({ variables: { id } });
      toast(t('forum.deleted', { ns: 'play', defaultValue: 'Publicación eliminada' }));
    } catch {
      toast(t('forum.errorDelete', { ns: 'play', defaultValue: 'No se pudo eliminar' }), 'error');
    }
  };

  const toggleFollow = (athlete) => {
    const mutation = athlete.is_followed ? unfollow : follow;
    mutation({ variables: { user_id: athlete.id } });
  };

  const openCompose = () => {
    setComposerRoutine(null);
    setComposerOpen(true);
  };

  const closePostMenu = () => setPostMenu({ anchor: null, routine: null });

  // Hero de composición: acento dorado pequeño + CTA blanco (premiumButtonSx).
  const composeCard = (
    <PremiumCard sx={{ mb: 2 }} padding={{ xs: 2.5, md: 3 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: 'gold.soft', flex: 'none' }}>
          <Bolt sx={{ color: 'gold.main', fontSize: 22 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.2 }}>
            {t('forum.composeHeadline', { ns: 'play', defaultValue: 'Comparte tu semana con la comunidad' })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('forum.composeSub', { ns: 'play', defaultValue: 'Inspira, entrena y supera tus límites.' })}
          </Typography>
        </Box>
      </Stack>
      {/* A 320 la columna pasa a ancho completo y `fullWidth` + py generoso convertía el CTA en
          un bloque enorme: en xs se acota y usa la etiqueta corta. */}
      <Button
        fullWidth
        size={isMobile ? 'medium' : 'large'}
        startIcon={<Add />}
        onClick={openCompose}
        sx={{ ...premiumButtonSx, py: { xs: 0.9, sm: 1.1 }, maxWidth: { xs: 260, sm: 'none' } }}>
        {isMobile
          ? t('forum.composeShort', { ns: 'play', defaultValue: 'Publicar' })
          : t('forum.compose', { ns: 'play', defaultValue: 'Publicar mi rutina' })}
      </Button>
    </PremiumCard>
  );

  const filterCard = (
    <Card sx={{ border: '1px solid', borderColor: 'hairline.main', borderRadius: 3, bgcolor: 'surface.content' }}>
      <CardContent>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.25 }}>
          <Tune sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {t('forum.filterTitle', { ns: 'play', defaultValue: 'Filtrar' })}
          </Typography>
        </Stack>
        <TextField select size="small" fullWidth label={t('forum.focus', { ns: 'play' })} value={muscleFocus} onChange={(e) => setMuscleFocus(e.target.value)}>
          <MenuItem value="">{t('forum.all', { ns: 'play' })}</MenuItem>
          {MUSCLE_GROUPS.map((m) => (
            <MenuItem key={m.value} value={m.value}>
              {muscleLabel(t, m.value)}
            </MenuItem>
          ))}
        </TextField>
      </CardContent>
    </Card>
  );

  const athleteRow = (a) => (
    <Stack key={a.id} direction="row" spacing={1.25} alignItems="center">
      <OnlineAvatar name={a.full_name} photo={a.photo} online={a.is_online} size={40} onClick={() => setAthleteId(a.id)} />
      <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => setAthleteId(a.id)}>
        <Typography variant="body2" fontWeight={700} noWrap>
          {a.full_name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {a.is_online
            ? t('forum.online', { ns: 'play', defaultValue: 'En línea' })
            : t('forum.routinesLabelCount', { ns: 'play', count: a.routines_count, defaultValue: `${a.routines_count} rutinas` })}
        </Typography>
      </Box>
      <Button
        size="small"
        onClick={() => toggleFollow(a)}
        sx={{ textTransform: 'none', fontWeight: 700, color: a.is_followed ? 'text.secondary' : 'gold.main', minWidth: 0 }}>
        {a.is_followed ? t('forum.unfollow', { ns: 'play', defaultValue: 'Siguiendo' }) : t('forum.follow', { ns: 'play', defaultValue: 'Seguir' })}
      </Button>
    </Stack>
  );

  const athletesCard = (
    <PremiumCard sx={{ position: 'relative', overflow: 'hidden' }}>
      {/* Emblema decorativo monocromo (bajísima opacidad), nunca protagonista. */}
      <EmojiEvents sx={{ position: 'absolute', right: -18, bottom: -18, fontSize: 130, color: 'gold.main', opacity: 0.05, pointerEvents: 'none' }} />
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Whatshot sx={{ color: 'gold.main', fontSize: 20 }} />
        <Typography variant="subtitle1" fontWeight={800}>
          {t('forum.discover', { ns: 'play', defaultValue: 'Descubre atletas' })}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {t('forum.followersLabel', { ns: 'play', count: followersCount, defaultValue: `${followersCount} te siguen` })}
      </Typography>
      {athletes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('forum.discoverEmpty', { ns: 'play', defaultValue: 'Aún no hay atletas para mostrar.' })}
        </Typography>
      ) : (
        <>
          <Stack spacing={1.5} divider={<Divider flexItem />}>
            {athletes.slice(0, 4).map(athleteRow)}
          </Stack>
          {athletes.length > 4 && (
            <Button
              fullWidth
              onClick={() => setAthletesDialogOpen(true)}
              sx={{ mt: 1.5, textTransform: 'none', fontWeight: 700, color: 'gold.main', justifyContent: 'center' }}>
              {t('forum.seeAllAthletes', { ns: 'play', defaultValue: 'Ver todos los atletas' })}
            </Button>
          )}
        </>
      )}
    </PremiumCard>
  );

  const renderPost = (r) => (
    <Card key={r.id} sx={{ border: '1px solid', borderColor: 'hairline.main', borderRadius: 3, bgcolor: 'surface.content', mb: 2, ...cardHoverSx }}>
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
          <OnlineAvatar name={r.user?.full_name || r.author_name} photo={r.user?.photo} online={r.user?.is_online} size={44} onClick={r.user?.id ? () => setAthleteId(r.user.id) : null} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={800} noWrap onClick={() => r.user?.id && setAthleteId(r.user.id)} sx={{ cursor: r.user?.id ? 'pointer' : 'default' }}>
                {r.user?.full_name || r.author_name}
              </Typography>
              {/* La entrenadora/admin publica con distintivo: guía la mirada sin gritar. */}
              {COACH_ROLES.includes(r.user?.role) && (
                <Chip
                  size="small"
                  label={t('forum.coach', { ns: 'play', defaultValue: 'Coach' })}
                  sx={{ height: 18, fontSize: '0.6rem', fontWeight: 800, bgcolor: 'gold.soft', color: 'gold.main', flex: 'none' }}
                />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {ago(r.created_at)}
            </Typography>
          </Box>
          <IconButton size="small" aria-label={t('forum.postMenu', { ns: 'play', defaultValue: 'Opciones' })} onClick={(e) => setPostMenu({ anchor: e.currentTarget, routine: r })} sx={{ color: 'text.secondary' }}>
            <MoreVert fontSize="small" />
          </IconButton>
        </Stack>

        {/* Sin título: la descripción rica lo contiene (imágenes/video incluidos). */}
        {r.description && <ContentRenderer value={r.description} sx={{ fontSize: '0.9rem' }} />}

        {/* Una publicación sin rutina (solo texto) no anuncia "0 ejercicios": no es una rutina. */}
        <Stack direction="row" spacing={0.75} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
          {r.muscle_focus && <Chip size="small" label={muscleLabel(t, r.muscle_focus)} sx={{ bgcolor: 'gold.soft', color: 'gold.main' }} />}
          {r.items?.length > 0 && (
            <Chip size="small" variant="outlined" label={t('forum.exercises', { ns: 'play', count: r.items.length })} />
          )}
          {r.items?.length > 0 && (
            <Chip size="small" variant="outlined" icon={<DownloadDone sx={{ fontSize: 16 }} />} label={t('forum.adoptions', { ns: 'play', count: r.adopt_count })} />
          )}
        </Stack>

        {/* La semana publicada (Lun..Dom): sin esto no se sabe QUÉ se está adoptando. */}
        {r.items?.length > 0 && <SharedRoutineWeek items={r.items} />}

        <Divider sx={{ my: 1.5 }} />

        <Stack direction="row" alignItems="center" spacing={0.5}>
          {/* El corazón da/quita like; el CONTADOR abre quiénes dieron like. */}
          <IconButton
            size="small"
            aria-label={t('forum.like', { ns: 'play', defaultValue: 'Me gusta' })}
            onClick={() => handleLike(r)}
            sx={{
              color: r.is_liked ? 'sessionStatus.absent.fg' : 'text.secondary',
              '&:active': { transform: 'scale(0.98)' },
              ...(r.is_liked && { '& svg': { animation: `${heartPop} 350ms cubic-bezier(0.32, 0.72, 0, 1)` } })
            }}>
            {r.is_liked ? <Favorite fontSize="small" /> : <FavoriteBorder fontSize="small" />}
          </IconButton>
          <Button
            size="small"
            color="inherit"
            disabled={!r.like_count}
            onClick={() => setLikersTarget(r)}
            aria-label={t('forum.seeLikers', { ns: 'play', defaultValue: 'Ver quién dio me gusta' })}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: 'text.secondary',
              minWidth: 0,
              px: 0.75,
              '&.Mui-disabled': { color: 'text.disabled' }
            }}>
            {r.like_count || 0}
          </Button>
          <Button
            size="small"
            color="inherit"
            startIcon={<ChatBubbleOutline />}
            onClick={() => setCommentsTarget(r)}
            sx={{ textTransform: 'none', fontWeight: 700, color: 'text.secondary', minWidth: 0 }}>
            {r.comment_count || 0}
          </Button>
        </Stack>

        {/* "Adoptar" en su propia fila: compartía línea con like/comentario y a 320 chocaban.
            Sin ejercicios no hay nada que adoptar: el botón desaparece en vez de mentir. */}
        {r.items?.length > 0 && (
          <Button
            fullWidth
            size="small"
            startIcon={<DownloadDone />}
            onClick={() => handleAdopt(r.id)}
            sx={{ ...premiumButtonSx, mt: 1.5, px: 2, py: 0.6 }}>
            {t('forum.adopt', { ns: 'play' })}
          </Button>
        )}

        <Divider sx={{ my: 1.5 }} />

        {/* Comentarios inline estilo IG (preview + "ver todos") */}
        <PostComments routineId={r.id} previewCount={2} onSeeAll={() => setCommentsTarget(r)} onOpenAuthor={(id) => setAthleteId(id)} />
      </CardContent>
    </Card>
  );

  const renderFeed = () => {
    if (loading && routines.length === 0) {
      return (
        <Stack spacing={2}>
          {[0, 1].map((i) => (
            <Skeleton key={i} variant="rounded" width="100%" height={220} sx={{ borderRadius: 3 }} />
          ))}
        </Stack>
      );
    }
    if (routines.length === 0) {
      return (
        <Box sx={{ py: 8, textAlign: 'center', color: 'text.secondary', border: '1px solid', borderColor: 'hairline.main', borderRadius: 3, bgcolor: 'surface.content' }}>
          <Groups sx={{ fontSize: 40, mb: 1 }} />
          <Typography variant="body1" fontWeight={600}>
            {t('forum.empty', { ns: 'play' })}
          </Typography>
          <Typography variant="body2">{t('forum.emptyHint', { ns: 'play' })}</Typography>
        </Box>
      );
    }
    return <>{sortedRoutines.map(renderPost)}</>;
  };

  const menuIsOwn = String(postMenu.routine?.user?.id) === String(me?.id);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Grid container spacing={2}>
        {/* Izquierda: publicar + filtro */}
        <Grid item xs={12} md={3}>
          <Box sx={{ position: { md: 'sticky' }, top: { md: 88 } }}>
            {composeCard}
            {filterCard}
          </Box>
        </Grid>

        {/* Centro: feed */}
        <Grid item xs={12} md={6}>
          {/* En mobile la columna de atletas no existe: carrusel horizontal compacto con
              presencia (tocar el avatar abre el perfil). */}
          {athletes.length > 0 && (
            <Stack
              direction="row"
              spacing={2}
              sx={{
                display: { xs: 'flex', md: 'none' },
                overflowX: 'auto',
                pb: 1.5,
                mb: 1,
                '&::-webkit-scrollbar': { display: 'none' }
              }}>
              {athletes.slice(0, 12).map((a) => (
                <Stack key={a.id} spacing={0.5} alignItems="center" sx={{ flex: 'none', width: 64 }}>
                  <OnlineAvatar name={a.full_name} photo={a.photo} online={a.is_online} size={48} onClick={() => setAthleteId(a.id)} />
                  <Typography variant="caption" sx={{ maxWidth: 64, fontWeight: 700 }} noWrap>
                    {a.full_name?.split(' ')[0]}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
          {/* El dropdown se llevaba 150px irreducibles de los ~288 de una pantalla de 320 y el
              título se partía en tres líneas: en xs baja a su propia fila. */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'flex-end' }}
            justifyContent="space-between"
            spacing={1.5}
            sx={{ mb: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.15 }}>
                {t('forum.heading', { ns: 'play' })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('forum.subheading', { ns: 'play' })}
              </Typography>
            </Box>
            <TextField
              select
              size="small"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              sx={{ minWidth: { sm: 150 }, flex: 'none' }}
              aria-label={t('forum.sortBy', { ns: 'play', defaultValue: 'Ordenar' })}>
              <MenuItem value="recent">{t('forum.sortRecent', { ns: 'play', defaultValue: 'Más recientes' })}</MenuItem>
              <MenuItem value="adopted">{t('forum.sortAdopted', { ns: 'play', defaultValue: 'Más adoptadas' })}</MenuItem>
            </TextField>
          </Stack>
          {renderFeed()}
        </Grid>

        {/* Derecha: atletas / seguidores */}
        <Grid item xs={12} md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
          <Box sx={{ position: { md: 'sticky' }, top: { md: 88 } }}>{athletesCard}</Box>
        </Grid>
      </Grid>

      {/* Menú de 3 puntos del post */}
      <Menu anchorEl={postMenu.anchor} open={Boolean(postMenu.anchor)} onClose={closePostMenu}>
        {menuIsOwn && (
          <MenuItem
            onClick={() => {
              setComposerRoutine(postMenu.routine);
              setComposerOpen(true);
              closePostMenu();
            }}>
            <EditOutlined fontSize="small" sx={{ mr: 1 }} />
            {t('forum.edit', { ns: 'play', defaultValue: 'Editar publicación' })}
          </MenuItem>
        )}
        {menuIsOwn && (
          <MenuItem
            onClick={() => {
              handleDelete(postMenu.routine.id);
              closePostMenu();
            }}
            sx={{ color: 'sessionStatus.absent.fg' }}>
            <DeleteOutline fontSize="small" sx={{ mr: 1 }} />
            {t('forum.delete', { ns: 'play', defaultValue: 'Eliminar' })}
          </MenuItem>
        )}
        {!menuIsOwn && (
          <MenuItem
            onClick={() => {
              handleReport(postMenu.routine.id);
              closePostMenu();
            }}>
            <FlagOutlined fontSize="small" sx={{ mr: 1 }} />
            {t('forum.report', { ns: 'play' })}
          </MenuItem>
        )}
      </Menu>

      {/* Ver todos los atletas */}
      <Dialog
        open={athletesDialogOpen}
        onClose={() => setAthletesDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3, backgroundColor: 'surface.content', backgroundImage: 'none', border: '1px solid', borderColor: 'hairline.main' } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'hairline.main' }}>
          <Typography variant="h6" fontWeight={800}>
            {t('forum.discover', { ns: 'play', defaultValue: 'Descubre atletas' })}
          </Typography>
          <IconButton onClick={() => setAthletesDialogOpen(false)} size="small" sx={{ color: 'text.secondary' }}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>
        <DialogContent>
          <Stack spacing={2} divider={<Divider flexItem />} sx={{ py: 1 }}>
            {athletes.map(athleteRow)}
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Quiénes dieron me gusta (se abre desde el contador del corazón) */}
      <RoutineLikersDialog
        open={Boolean(likersTarget)}
        routineId={likersTarget?.id}
        onClose={() => setLikersTarget(null)}
        onOpenAuthor={(id) => {
          setLikersTarget(null);
          setAthleteId(id);
        }}
      />

      <RoutineCommentsDialog open={Boolean(commentsTarget)} routine={commentsTarget} onClose={() => setCommentsTarget(null)} onOpenAuthor={(id) => setAthleteId(id)} />
      <PublishRoutineDialog open={composerOpen} onClose={() => setComposerOpen(false)} routine={composerRoutine} feedVars={feedVars} />
      <PlayAthleteModal userId={athleteId} open={Boolean(athleteId)} onClose={() => setAthleteId(null)} />
    </Box>
  );
}

export default SharedRoutinesForum;
